import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Search, Home, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
interface FormData {
  homeValue: string;
  address: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
interface MultiStepFormProps {
  onStepChange?: (step: number, data?: {
    loanAmount: number;
    homeValue: string;
  }) => void;
}
const SIMULATED_ADDRESSES = ['123 Queen St W, Toronto, ON', '456 Yonge St, Mississauga, ON', '789 King St E, Hamilton, ON', '101 Dundas St, Oakville, ON', '202 Main St, Markham, ON', '303 Maple Ave, Vaughan, ON', '404 Pine St, Burlington, ON', '505 Cedar Ln, Richmond Hill, ON'];
// Confetti Animation Component
const Confetti = () => {
  return <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden z-0" data-id="element-232">
      {Array.from({
      length: 24
    }).map((_, i) => {
      const angle = i * 360 / 24;
      const radius = 120 + Math.random() * 80;
      return <motion.div key={i} initial={{
        opacity: 1,
        scale: 0,
        x: 0,
        y: 0
      }} animate={{
        opacity: 0,
        scale: Math.random() * 0.6 + 0.4,
        x: Math.cos(angle * Math.PI / 180) * radius,
        y: Math.sin(angle * Math.PI / 180) * radius
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} className={`absolute w-3 h-3 rounded-full ${i % 2 === 0 ? 'bg-accent' : 'bg-primary'}`} data-id="element-233" />;
    })}
    </div>;
};
export function MultiStepForm({
  onStepChange
}: MultiStepFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    homeValue: '',
    address: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredAddresses, setFilteredAddresses] = useState<string[]>([]);
  const addressRef = useRef<HTMLDivElement>(null);
  const changeStep = (newStep: number) => {
    setStep(newStep);
    const loanAmount = calculateLoanAmount();
    onStepChange?.(newStep, {
      loanAmount,
      homeValue: formData.homeValue
    });
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressRef.current && !addressRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleAddressChange = (value: string) => {
    updateFormData('address', value);
    if (value.length > 0) {
      const filtered = SIMULATED_ADDRESSES.filter(addr => addr.toLowerCase().includes(value.toLowerCase()));
      setFilteredAddresses(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  const selectAddress = (address: string) => {
    updateFormData('address', address);
    setShowSuggestions(false);
  };
  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return '$0';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(num);
  };
  const handleHomeValueChange = (value: string) => {
    // Remove non-digits
    const numericValue = value.replace(/\D/g, '');
    if (numericValue) {
      // Format with commas
      const formatted = new Intl.NumberFormat('en-US').format(parseInt(numericValue, 10));
      updateFormData('homeValue', formatted);
    } else {
      updateFormData('homeValue', '');
    }
  };
  const validateStep1 = () => {
    const newErrors: Partial<FormData> = {};
    const numericHomeValue = parseFloat(formData.homeValue.replace(/,/g, ''));
    if (!formData.homeValue || isNaN(numericHomeValue) || numericHomeValue <= 0) {
      newErrors.homeValue = 'Please enter a valid home value';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Please enter your property address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const validateStep2 = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    if (!formData.phone.trim() || !/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Valid phone number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNext = () => {
    if (validateStep1()) {
      changeStep(2);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) {
      return;
    }
    if (!agreedToTerms) {
      alert('Please agree to the Terms and Conditions');
      return;
    }
    console.log('Form submitted:', formData);
    changeStep(3);
  };
  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined
      });
    }
  };
  const calculateLoanAmount = () => {
    const value = parseFloat(formData.homeValue.replace(/,/g, ''));
    if (isNaN(value)) return 0;
    return value * 0.8; // 80% LTV
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 30 : -30,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 30 : -30,
      opacity: 0
    })
  };
  return <div className="w-full relative" id="form" data-id="element-234">
      {/* Premium Gradient Border Container */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/40 via-primary/10 to-primary/30 rounded-[2rem] transform rotate-1 scale-[1.02] opacity-50 blur-sm" data-id="element-235"></div>

      <div className="bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-2xl p-8 md:p-12 w-full relative border border-white/50" data-id="element-236">
        {/* Step Indicators - Dash bars */}
        {step < 3 && <div className="flex justify-center items-center mb-8 space-x-3" data-id="element-237">
            <div className={`h-2 w-16 rounded-full transition-colors duration-300 ${step >= 1 ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-238" />
            <div className={`h-2 w-16 rounded-full transition-colors duration-300 ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-239" />
          </div>}

        <AnimatePresence mode="wait" custom={step} data-id="element-240">
          {step === 1 && <motion.div key="step1" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="space-y-8" data-id="element-241">
              <div className="text-center mb-4" data-id="element-242">
                <div className="inline-flex items-center gap-3 mb-2" data-id="element-243">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center" data-id="element-244">
                    <Home className="w-5 h-5 text-primary" data-id="element-245" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-serif font-semibold text-gray-900" data-id="element-246">
                    Property Details
                  </h3>
                </div>
                <p className="text-gray-500 text-sm" data-id="element-247">
                  Enter your property information to calculate your equity.
                </p>
              </div>

              <div className="space-y-6 max-w-xl mx-auto" data-id="element-248">
                <Input label="Estimated Home Value" type="text" prefix={<span className="text-gray-500 font-medium" data-id="element-250">$</span>} placeholder="500,000" value={formData.homeValue} onChange={handleHomeValueChange} error={errors.homeValue} required data-id="element-249" />

                <div className="relative" ref={addressRef} data-id="element-251">
                  <Input label="Property Address" type="text" prefix={<Search className="w-5 h-5 text-gray-400" data-id="element-253" />} placeholder="Start typing your address..." value={formData.address} onChange={handleAddressChange} error={errors.address} required data-id="element-252" />

                  {/* Autocomplete Dropdown */}
                  <AnimatePresence data-id="element-254">
                    {showSuggestions && filteredAddresses.length > 0 && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} className="absolute z-20 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl max-h-60 overflow-y-auto" data-id="element-255">
                        {filteredAddresses.map((addr, idx) => <div key={idx} className="px-5 py-3.5 hover:bg-gray-50 cursor-pointer flex items-center text-gray-700 border-b border-gray-50 last:border-0 transition-colors" onClick={() => selectAddress(addr)} data-id="element-256">
                            <MapPin className="w-4 h-4 text-primary/60 mr-3 flex-shrink-0" data-id="element-257" />
                            <span className="truncate font-medium" data-id="element-258">{addr}</span>
                          </div>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="pt-6 max-w-xl mx-auto" data-id="element-259">
                <div className="relative group" data-id="element-260">
                  <div className="absolute -inset-1 bg-gradient-to-r from-accent to-primary rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" data-id="element-261"></div>
                  <Button type="button" variant="secondary" size="lg" className="w-full relative" onClick={handleNext} icon data-id="element-262">
                    Check How Much More You Can Borrow
                  </Button>
                </div>
              </div>
            </motion.div>}

          {step === 2 && <motion.div key="step2" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-263">
              <button onClick={() => changeStep(1)} className="text-sm text-gray-400 hover:text-primary mb-6 flex items-center transition-colors font-medium" data-id="element-264">
                ← Back to Property Details
              </button>

              {/* Contact Form only - no result card here */}
              <div data-id="element-265">
                <h3 className="text-xl font-serif font-semibold text-gray-900 mb-1" data-id="element-266">
                  Where should we send your detailed report?
                </h3>
                <p className="text-gray-500 text-sm mb-5" data-id="element-267">
                  Enter your details below to proceed.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-268">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-269">
                    <Input label="First Name" type="text" placeholder="John" value={formData.firstName} onChange={value => updateFormData('firstName', value)} error={errors.firstName} required data-id="element-270" />
                    <Input label="Last Name" type="text" placeholder="Smith" value={formData.lastName} onChange={value => updateFormData('lastName', value)} error={errors.lastName} required data-id="element-271" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-272">
                    <Input label="Email" type="email" placeholder="john@email.com" value={formData.email} onChange={value => updateFormData('email', value)} error={errors.email} required data-id="element-273" />
                    <Input label="Phone/Mobile" type="tel" placeholder="(647) 000-0000" value={formData.phone} onChange={value => updateFormData('phone', value)} error={errors.phone} required data-id="element-274" />
                  </div>

                  <div className="flex items-start pt-3 pb-1" data-id="element-275">
                    <input type="checkbox" id="terms" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer" data-id="element-276" />
                    <label htmlFor="terms" className="ml-3 text-sm text-gray-500 leading-relaxed cursor-pointer" data-id="element-277">
                      By clicking "Submit" you agree that you are submitting an
                      inquiry to EquityRich.ca, and you agree to our{' '}
                      <a href="/terms" className="text-primary font-medium hover:text-primary-dark underline-offset-2 hover:underline" data-id="element-278">
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-primary font-medium hover:text-primary-dark underline-offset-2 hover:underline" data-id="element-279">
                        Privacy Policy
                      </a>
                    </label>
                  </div>

                  <div className="pt-1" data-id="element-280">
                    <Button type="submit" variant="secondary" size="lg" className="w-full shadow-lg shadow-primary/20" icon data-id="element-281">
                      Submit Application
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>}

          {step === 3 && <motion.div key="step3" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="flex flex-col items-center justify-center text-center py-12 relative" data-id="element-282">
              <Confetti data-id="element-283" />

              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-8 relative z-10" data-id="element-284">
                <CheckCircle className="w-10 h-10 text-green-500" data-id="element-285" />
              </motion.div>

              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 relative z-10" data-id="element-286">
                Great News!
              </h2>
              <h3 className="text-2xl md:text-3xl font-medium text-gray-600 mb-8 relative z-10" data-id="element-287">
                You could potentially unlock
              </h3>

              <div className="text-6xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-accent to-yellow-600 mb-12 tracking-tight relative z-10 py-2" data-id="element-288">
                {formatCurrency(calculateLoanAmount())}
              </div>

              <p className="text-xl md:text-2xl font-bold text-gray-900 max-w-lg mx-auto leading-relaxed relative z-10" data-id="element-289">
                Your dedicated representative will be in touch shortly to assist
                you!
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}